<template>
  <div class="yama-game-stage">
    <!-- Dynamic Stage Component -->
    <component
      :is="currentStageComponent"
      :inject-key="injectKey"
      :is-end="isEnd"
      :game-stats="gameStats"
      @choose-option="$emit('choose-option', $event)"
      @finish-answer="$emit('finish-answer')"
      v-bind="stageProps"
    />
  </div>
</template>

<script>
// Import existing stage components
import StageInit from '../stageInit.vue';
import StageGameStart from '../stageGameStart/index.vue';

/**
 * Yama Game Stage wrapper component following SOLID principles
 * Single Responsibility: Manages dynamic stage component rendering
 * Open/Closed Principle: Easy to add new stages without modification
 * Dependency Inversion: Depends on stage component abstractions
 */
export default {
  name: 'YamaGameStage',
  
  components: {
    StageInit,
    StageGameStart
  },
  
  props: {
    currentStageName: {
      type: String,
      default: 'stage-init'
    },
    injectKey: {
      type: Object,
      default: () => ({})
    },
    isEnd: {
      type: Boolean,
      default: false
    },
    gameStats: {
      type: Object,
      default: () => ({})
    }
  },
  
  emits: [
    'choose-option',
    'finish-answer'
  ],
  
  computed: {
    /**
     * Map stage name to component
     * Open/Closed Principle: Easy to extend with new stages
     */
    currentStageComponent() {
      const stageMap = {
        'stage-init': 'StageInit',
        'stage-game-start': 'StageGameStart'
      };
      
      return stageMap[this.currentStageName] || 'StageInit';
    },
    
    /**
     * Props to pass to stage components
     * Interface Segregation: Only necessary props
     */
    stageProps() {
      return {
        // Common props for all stages
        gameStats: this.gameStats,
        isEnd: this.isEnd,
        
        // Stage-specific props can be added here
        ...(this.getStageSpecificProps())
      };
    }
  },
  
  methods: {
    /**
     * Get stage-specific props
     * Open/Closed Principle: Easy to extend for new stages
     */
    getStageSpecificProps() {
      switch (this.currentStageName) {
        case 'stage-init':
          return {
            // Init stage specific props
          };
        case 'stage-game-start':
          return {
            // Game start stage specific props
          };
        default:
          return {};
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.yama-game-stage {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  
  // Ensure stage components fill available space
  > * {
    flex: 1;
  }
}

// Stage transition animations
.yama-game-stage {
  .stage-enter-active,
  .stage-leave-active {
    transition: all 0.5s ease-in-out;
  }
  
  .stage-enter {
    opacity: 0;
    transform: translateX(30px);
  }
  
  .stage-leave-to {
    opacity: 0;
    transform: translateX(-30px);
  }
}

// Loading states for stages
.yama-game-stage {
  &.loading {
    .stage-component {
      opacity: 0.6;
      pointer-events: none;
      
      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 40px;
        height: 40px;
        border: 3px solid #f3f3f3;
        border-top: 3px solid #007bff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
    }
  }
}

@keyframes spin {
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
}

// Responsive design
@media (max-width: 768px) {
  .yama-game-stage {
    // Mobile-specific adjustments
    min-height: 400px;
  }
}
</style>